import { MARKETING_CATEGORY_LABELS, type MarketingCategory, type NewsletterReach, type NewsletterTarget } from '@evi/shared';

/**
 * The newsletter page's approval pieces (23 October 2026, review). Approval
 * is the only human gate on agent mail to drivers, so the person approving
 * sees exactly what they approve (audience, category, reach), and the page
 * sends the draft's version with Approve: the Api refuses an approval of a
 * draft that changed since the page loaded it.
 */

/** "Drivers: all enrolled", "Lead audience: investor", or the segment filters. */
export function targetSummary(t: NewsletterTarget): string {
  if (t.type === 'lead_audience') return `Lead audience: ${t.audience}`;
  const seg = t.segment ?? {};
  const parts = Object.entries(seg)
    .filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('|') : String(v)}`);
  // Drivers are enrolled by default (23 October 2026); the send gate leaves
  // out anyone unsubscribed, with the category off, or capped.
  return parts.length ? `Drivers: ${parts.join(', ')}` : 'Drivers: all enrolled';
}

/** The Approve confirm: what is being approved, and who it reaches now. */
export function approveConfirmText(d: {
  subject: string;
  target: NewsletterTarget;
  category: MarketingCategory;
  reach: NewsletterReach | null;
}): string {
  const reach = d.reach
    ? `Reach now: ${d.reach.reachable} reachable of ${d.reach.total_targeted} targeted (${d.reach.excluded_for_consent} excluded for consent).`
    : 'Reach: not available.';
  return [
    `Approve "${d.subject}"?`,
    `Audience: ${targetSummary(d.target)}.`,
    `Category: ${MARKETING_CATEGORY_LABELS[d.category]}.`,
    reach,
    'Approval is a human sign-off: after this it can be sent, from here or by the agent.',
  ].join('\n');
}

/** The Api's answer when the draft changed after the page loaded it. */
export const DRAFT_CHANGED_CODE = 'DRAFT_CHANGED';

/** The draft version the reviewer was shown. */
export interface ShownDraft { id: string; version: string }

/**
 * What the page does with a freshly fetched draft (23 October 2026, review).
 * The page pins the version the reviewer was shown and sends THAT with
 * Approve. A refetch (the tab regaining focus after 30 s, a reconnect) used
 * to swap the editor to a newer version with no notice, and Approve then
 * sent the newer version, so a body the agent rewrote mid-review was
 * approved unread. Now:
 *   'hydrate'  nothing is shown yet, another draft was opened, the reviewer
 *              asked to reload or just saved, or the content is unchanged
 *              (the status may have moved): show it and pin its version;
 *   'changed'  a different version arrived on its own: keep what the
 *              reviewer read on screen, and say it changed.
 */
export function draftHydrationStep(
  shown: ShownDraft | null,
  detail: ShownDraft,
  acceptNewVersion: boolean,
): 'hydrate' | 'changed' {
  if (!shown || shown.id !== detail.id || acceptNewVersion) return 'hydrate';
  return shown.version === detail.version ? 'hydrate' : 'changed';
}

/** The banner shown while the page holds an older version than the server. */
export const DRAFT_CHANGED_NOTICE = 'This draft changed since you opened it (the agent or another person edited it). '
  + 'What you see is the version you read. Reload to review the new version before approving.';
